import { QueryClient } from "@tanstack/react-query";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import { StaticContent } from "@/types/dto/pdp/pdp-contentful";

// Component-specific prefetch functions
import prefetchTourHeader from "./tour-header/prefetch";
import prefetchTourMediaGallery from "./tour-media-gallery/prefetch";
import prefetchItinerary from "./itinerary-section/prefetch";
import prefetchTourSummary from "./tour-summary/prefetch";
import prefetchKeyInformation from "./key-information/prefetch";
import prefetchAccommodations from "./accommodations/prefetch";

interface PrefetchParams {
  queryClient: QueryClient;
  tourDate: TourDate;
  neoId: string;
  staticContent: StaticContent;
  currencyId: string;
  marketId: string;
}

/**
 * Centralized prefetch orchestrator for all PDP components
 * This function coordinates prefetching data for all components when a tour date changes
 */
export const prefetchPdpComponents = ({
  queryClient,
  tourDate,
  neoId,
  staticContent,
  currencyId,
  marketId
}: PrefetchParams) => {
  const { packageId, packageDateId } = tourDate;

  // Prefetch all component data in parallel
  Promise.all([
    // Tour Header
    prefetchTourHeader(queryClient, packageId, packageDateId, neoId, staticContent, currencyId, marketId),

    // Accommodations
    prefetchAccommodations(queryClient, packageId, packageDateId, neoId, currencyId, marketId),

    // Itinerary
    prefetchItinerary(queryClient, packageId, packageDateId, neoId, currencyId, marketId, staticContent),

    // Tour Summary
    prefetchTourSummary(queryClient, packageId, packageDateId, neoId, marketId, currencyId, staticContent),

    // Media Gallery
    prefetchTourMediaGallery(queryClient, packageId, packageDateId, neoId),

    // Key Information
    prefetchKeyInformation(queryClient, packageId, neoId, currencyId, marketId)
  ]).catch(() => {
    // Silently handle prefetch errors to avoid breaking the UI
    // Errors are expected for components that may not be ready yet
  });
};

/**
 * Individual component prefetch functions for more granular control
 */
export {
  prefetchTourHeader,
  prefetchTourMediaGallery,
  prefetchItinerary,
  prefetchTourSummary,
  prefetchKeyInformation,
  prefetchAccommodations
};